import Picto from '../Picto/Picto';
import styles from './CircleProgressIndicator.module.css';

/**
 * CircleProgressIndicator - Shows waiting / in-progress / complete state with a Picto in a circle.
 * @param {object} props
 * @param {number} props.completed - Number of completed steps
 * @param {number} props.total - Total number of steps
 * @param {boolean} [props.hasErrors=false] - Whether errors occurred during processing (affects the complete state visual)
 * @param {string} [props.className] - Additional CSS classes
 * @param {number} [props.size=32] - Size in pixels (width/height)
 * @returns {JSX.Element} CircleProgressIndicator component
 */
function CircleProgressIndicator({
  completed,
  total,
  hasErrors = false,
  className = '',
  size = 32,
}) {
  const isComplete = total > 0 && completed >= total;
  const isWaiting = total > 0 && completed === 0;

  let state = 'in-progress';
  /** @type {'tick-square' | 'clock' | 'refresh' | 'info-circle'} */
  let icon = 'refresh';
  if (isComplete && hasErrors) {
    state = 'complete-with-errors';
    icon = 'info-circle';
  } else if (isComplete) {
    state = 'complete';
    icon = 'tick-square';
  } else if (isWaiting) {
    state = 'waiting';
    icon = 'clock';
  }

  const progressRatio = total > 0 ? completed / total : 0;
  const r = size / 2 - 2;
  const circumference = 2 * Math.PI * r;
  const strokeDashoffset = circumference * (1 - progressRatio);

  return (
    <div
      className={`${styles.wrapper} ${styles[state]} ${className}`.trim()}
      style={{ width: size, height: size }}
      role="progressbar"
    >
      {state === 'in-progress' && (
        <svg className={styles.ring} width={size} height={size}>
          <circle className={styles['ring-track']} cx={size / 2} cy={size / 2} r={r} />
          <circle
            className={styles['ring-progress']}
            cx={size / 2}
            cy={size / 2}
            r={r}
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
          />
        </svg>
      )}
      <Picto icon={icon} className={styles.icon} />
    </div>
  );
}

export default CircleProgressIndicator;
